import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShoppingBag, Check, Plus } from "lucide-react";
import api, { resolveImage } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import MoonAvatar from "@/components/MoonAvatar";

const DEFAULT_BUNDLE_DISCOUNT = 0.25; // Fallback if /config fails

/**
 * Moon's "Frequently Bought Together" widget.
 * Fetches 2 companion products from GET /api/products/{id}/bought-together,
 * displays current + companions with checkboxes, live-updates total price,
 * and applies the admin-configured bundle discount (from /api/config) when
 * ALL items are selected.
 */
export default function FrequentlyBoughtTogether({ product }) {
  const [companions, setCompanions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [discountRate, setDiscountRate] = useState(DEFAULT_BUNDLE_DISCOUNT);
  const { add } = useCart();

  useEffect(() => {
    api.get("/config").then(({ data }) => {
      const pct = Number(data?.bundle_discount_pct);
      if (Number.isFinite(pct) && pct >= 0) setDiscountRate(pct / 100);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!product?.id) return;
    setLoading(true);
    api
      .get(`/products/${product.id}/bought-together`)
      .then(({ data }) => {
        const list = Array.isArray(data) ? data.filter(Boolean) : [];
        setCompanions(list);
        // Preselect current + all companions by default
        setSelectedIds(new Set([product.id, ...list.map((p) => p.id)]));
      })
      .catch(() => setCompanions([]))
      .finally(() => setLoading(false));
  }, [product?.id]);

  const all = useMemo(() => [product, ...companions].filter(Boolean), [product, companions]);
  const chosen = all.filter((p) => selectedIds.has(p.id));

  const subtotal = chosen.reduce((s, p) => s + Number(p.price || 0), 0);
  const allChecked = chosen.length === all.length && all.length >= 2;
  const discount = allChecked ? subtotal * discountRate : 0;
  const total = subtotal - discount;
  const discountPctLabel = Math.round(discountRate * 100);

  if (loading) {
    return (
      <div className="mt-8 p-4 rounded-lg border border-sj-gold/20 bg-white" data-testid="fbt-loading">
        <div className="text-xs tracking-[0.2em] uppercase text-sj-goldDark">Moon is styling your set…</div>
      </div>
    );
  }
  if (companions.length === 0) return null;

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const addSelected = () => {
    if (chosen.length === 0) {
      toast.error("Please select at least one item");
      return;
    }
    chosen.forEach((p) => add(p, 1));
    toast.success(
      allChecked
        ? `Bundle added — ${discountPctLabel}% off applied on ${chosen.length} pieces`
        : `${chosen.length} item${chosen.length > 1 ? "s" : ""} added to bag`
    );
  };

  return (
    <section
      className="mt-8 sm:mt-10 max-w-3xl p-4 sm:p-5 rounded-xl border border-sj-gold/25 bg-white shadow-sm"
      data-testid="fbt-section"
    >
      {/* Header — Moon-branded */}
      <div className="flex items-center gap-2.5">
        <MoonAvatar size={26} />
        <div>
          <h3 className="font-display text-base sm:text-lg font-light text-sj-ink leading-tight">
            Moon's Pairings
          </h3>
          <div className="text-[10px] tracking-[0.14em] uppercase text-sj-inkSoft mt-0.5">
            Complete the look — picked by Moon
          </div>
        </div>
      </div>

      <div
        className="mt-3 inline-block px-2.5 py-1 rounded-md border border-sj-gold/40 bg-sj-gold/10 text-[11px] text-sj-goldDark tracking-wide"
        data-testid="fbt-discount-note"
      >
        Save an extra {discountPctLabel}% on buying these products together!
      </div>

      {/* Row of product tiles with + between (compact) */}
      <div className="mt-4 flex items-start gap-2">
        {all.map((p, i) => (
          <React.Fragment key={p.id}>
            <div className="flex-1 min-w-0 max-w-[130px]" data-testid={`fbt-tile-${p.id}`}>
              {(() => {
                const imgEl = (
                  <>
                    {p.images?.[0] ? (
                      <img
                        src={resolveImage(p.images[0], { w: 240 })}
                        alt={p.name}
                        className="w-full h-full object-contain"
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[10px] text-sj-muted">
                        No image
                      </div>
                    )}
                    {/* Checkbox overlay */}
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle(p.id); }}
                      aria-pressed={selectedIds.has(p.id)}
                      aria-label={selectedIds.has(p.id) ? `Remove ${p.name} from bundle` : `Add ${p.name} to bundle`}
                      className={`absolute top-1.5 left-1.5 h-5 w-5 rounded border-2 flex items-center justify-center transition-colors ${
                        selectedIds.has(p.id)
                          ? "bg-sj-goldDark border-sj-goldDark text-white"
                          : "bg-white/95 border-sj-ink/30 text-transparent"
                      }`}
                      data-testid={`fbt-check-${p.id}`}
                    >
                      <Check size={11} strokeWidth={3} />
                    </button>
                    {/* "This item" pill on the current product */}
                    {i === 0 && (
                      <div className="absolute bottom-1.5 left-1.5 text-[9px] tracking-wider uppercase bg-white/95 px-1.5 py-0.5 rounded-full text-sj-ink border border-sj-ink/10">
                        This item
                      </div>
                    )}
                  </>
                );
                // Current product (i === 0) is the one the customer is already viewing — don't link.
                // Companion products get a Link wrapper on the whole image tile.
                if (i === 0) {
                  return (
                    <div className="relative rounded-md overflow-hidden bg-white border border-sj-ink/5 aspect-square">
                      {imgEl}
                    </div>
                  );
                }
                return (
                  <Link
                    to={`/product/${p.id}`}
                    className="relative block rounded-md overflow-hidden bg-white border border-sj-ink/5 aspect-square hover:border-sj-goldDark transition-colors"
                    data-testid={`fbt-image-link-${p.id}`}
                    aria-label={`Open ${p.name}`}
                  >
                    {imgEl}
                  </Link>
                );
              })()}
              <Link
                to={`/product/${p.id}`}
                className="mt-1.5 block text-[11px] sm:text-xs text-sj-ink leading-snug line-clamp-2 hover:text-sj-goldDark transition-colors"
                data-testid={`fbt-name-${p.id}`}
              >
                {p.name}
              </Link>
              <div className="mt-0.5 text-xs font-medium text-sj-ink" data-testid={`fbt-price-${p.id}`}>
                ₹{Number(p.price).toLocaleString("en-IN")}
              </div>
            </div>

            {i < all.length - 1 && (
              <div className="flex items-center justify-center text-sj-goldDark shrink-0 pt-10 sm:pt-14">
                <Plus size={14} />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Total + CTA */}
      <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-3 border-t border-sj-gold/15">
        <div className="text-xs">
          <span className="text-sj-inkSoft">Total: </span>
          {allChecked && (
            <span className="text-sj-muted line-through mr-1" data-testid="fbt-mrp">
              ₹{Number(subtotal).toLocaleString("en-IN")}
            </span>
          )}
          <span className="text-base font-display font-medium text-sj-ink" data-testid="fbt-total">
            ₹{Number(Math.round(total)).toLocaleString("en-IN")}
          </span>
          {allChecked && (
            <span className="ml-1.5 text-[10px] text-sj-goldDark tracking-wide">You save ₹{Number(Math.round(discount)).toLocaleString("en-IN")}</span>
          )}
        </div>
        <button
          type="button"
          onClick={addSelected}
          className="btn-primary justify-center whitespace-nowrap text-xs px-4 py-2"
          data-testid="fbt-add-selected"
        >
          <ShoppingBag size={13} /> Add selected to cart
        </button>
      </div>
    </section>
  );
}
